import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { variant, space, layout } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { Icon } from '../icon/Icon';

const buttonSizeStyle = variant({
  prop: 'size',
  key: 'buttons.size',
});

const buttonStyle = variant({
  key: 'buttons',
});

const StyledIcon = styled(Icon)`
  margin-right: 4px;
  color: inherit;
`;

const Text = styled.span`
  display: inline-block;
  pointer-events: none;
  user-select: none;
  font-size: ${themeGet('button.fontSize')};

  & + ${StyledIcon} {
    margin-right: 0;
    margin-left: 4px;
  }
`;

export const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  outline: 0;
  box-shadow: ${themeGet('button.boxShadow')};
  border: ${themeGet('button.border')};
  border-radius: ${themeGet('button.borderRadius')};
  border-top: ${themeGet('button.borderTop')};
  border-bottom: ${themeGet('button.borderBottom')};
  border-left: ${themeGet('button.borderLeft')};
  border-right: ${themeGet('button.borderRight')};
  cursor: pointer;
  text-decoration: none;

  ${buttonStyle}
  ${buttonSizeStyle}
  ${space}
  ${layout}

  ${StyledIcon} {
    width: ${themeGet('button.iconSize')};
    height: ${themeGet('button.iconSize')};
    font-size: ${themeGet('button.iconSize')};
  }

  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

/**
 * @typedef ButtonProps
 * @property {'default' | 'primary' | 'link'} variant
 * @property {'default' | 'small' | 'large'} size
 * @property {string} prefixIcon
 * @property {string} suffixIcon
 */
/**
 * @param {ButtonProps} props
 */
export const Button = forwardRef(function Button(
  { variant, size, children, prefixIcon, suffixIcon, ...rest },
  ref
) {
  return (
    <StyledButton ref={ref} variant={variant} size={size} {...rest}>
      {prefixIcon && <StyledIcon name={prefixIcon} />}
      <Text>{children}</Text>
      {suffixIcon && <StyledIcon name={suffixIcon} />}
    </StyledButton>
  );
});

Button.defaultProps = {
  variant: 'default',
  size: 'default',
};
